import { Fragment, useCallback, useEffect, useState } from "react";
import { invokeAdmin, errMsg } from "../lib/adminApi";

// The DQ review queue: quarantined event.events rows awaiting an operator
// decision (publish | reject | merge into a probable original). Data comes
// from admin_quarantine_list; actions go through admin_quarantine_resolve.
// Spec: docs/specs/pipeline-data-quality.md (admin surface).

const REASONS = [
  "LOW_CONFIDENCE_DUPLICATE",
  "OFF_TAXONOMY",
  "VALIDATION_FAILED_UNCORRECTABLE",
  "PROMO_SUSPECT",
  "LOW_EXTRACTION_CONFIDENCE",
] as const;
type QuarantineReason = (typeof REASONS)[number];

type Confidence = {
  temporal?: number;
  classification?: number;
  place?: number;
  duplicate?: number;
} | null;

type QuarantinedEvent = {
  id: string;
  props: Record<string, unknown>;
  place_id: string | null;
  place_name: string | null;
  created: string;
  quarantine_reason: QuarantineReason;
  confidence: Confidence;
  duplicate_of: string | null;
};

const PAGE_SIZE = 50;

/** Short human label for the reason badge (full code in the title tooltip). */
const REASON_LABELS: Record<QuarantineReason, string> = {
  LOW_CONFIDENCE_DUPLICATE: "dup?",
  OFF_TAXONOMY: "off-taxonomy",
  VALIDATION_FAILED_UNCORRECTABLE: "invalid",
  PROMO_SUSPECT: "promo?",
  LOW_EXTRACTION_CONFIDENCE: "low-conf",
};

function reasonBadgeClass(reason: QuarantineReason): string {
  switch (reason) {
    case "LOW_CONFIDENCE_DUPLICATE":
      return "bg-amber-950 text-amber-300 border border-amber-900";
    case "OFF_TAXONOMY":
      return "bg-purple-950 text-purple-300 border border-purple-900";
    case "VALIDATION_FAILED_UNCORRECTABLE":
      return "bg-red-950 text-red-300 border border-red-900";
    case "PROMO_SUSPECT":
      return "bg-orange-950 text-orange-300 border border-orange-900";
    default:
      return "bg-zinc-800 text-zinc-400 border border-zinc-700";
  }
}

/** Confidence chips: temporal/classification/place/duplicate as 0.72 numerics. */
function ConfidenceChips({ confidence }: { confidence: Confidence }) {
  if (!confidence) return <span className="text-zinc-600">—</span>;
  const dims: Array<[string, number | undefined]> = [
    ["t", confidence.temporal],
    ["c", confidence.classification],
    ["p", confidence.place],
    ["d", confidence.duplicate],
  ];
  const titles: Record<string, string> = {
    t: "temporal",
    c: "classification",
    p: "place",
    d: "duplicate",
  };
  return (
    <span className="flex flex-wrap gap-1">
      {dims
        .filter(([, v]) => typeof v === "number")
        .map(([k, v]) => (
          <span
            key={k}
            title={titles[k]}
            className={`rounded px-1 py-0.5 font-mono text-xs ${
              (v as number) < 0.5
                ? "bg-red-950 text-red-300"
                : "bg-zinc-800 text-zinc-300"
            }`}
          >
            {k}:{(v as number).toFixed(2)}
          </span>
        ))}
    </span>
  );
}

export default function ReviewPage() {
  const [reason, setReason] = useState<string>("");
  const [q, setQ] = useState("");
  const [events, setEvents] = useState<QuarantinedEvent[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** id of the row whose action is in flight (disables its buttons). */
  const [busyId, setBusyId] = useState<string | null>(null);
  /** id of the row whose merge panel is open. */
  const [mergeId, setMergeId] = useState<string | null>(null);
  const [mergeTarget, setMergeTarget] = useState("");

  const load = useCallback(async (newOffset = 0, append = false) => {
    setLoading(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { limit: PAGE_SIZE, offset: newOffset };
      if (reason) body.reason = reason;
      if (q.trim()) body.q = q.trim();
      const data = await invokeAdmin<{ events: QuarantinedEvent[]; total: number }>(
        "admin_quarantine_list",
        body,
      );
      setEvents((prev) => (append ? [...prev, ...data.events] : data.events));
      setTotal(data.total);
      setOffset(newOffset + data.events.length);
      setHasMore(data.events.length === PAGE_SIZE);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [reason, q]);

  useEffect(() => { load(0, false); }, [reason]); // eslint-disable-line react-hooks/exhaustive-deps, react-hooks/set-state-in-effect -- refetch on tab change; loader's sync setLoading/setError reset is intentional (shared with Refresh); data state is only written after the awaited request

  /** Resolve one row, optimistically remove it, then refetch page 1. */
  const resolve = useCallback(
    async (id: string, action: "publish" | "reject" | "merge_into", targetEventId?: string) => {
      setBusyId(id);
      setError(null);
      try {
        const body: Record<string, unknown> = { id, action };
        if (targetEventId) body.targetEventId = targetEventId;
        await invokeAdmin<{ ok: boolean }>("admin_quarantine_resolve", body);
        setEvents((prev) => prev.filter((ev) => ev.id !== id));
        setTotal((t) => (t !== null && t > 0 ? t - 1 : t));
        setMergeId(null);
        setMergeTarget("");
        await load(0, false);
      } catch (e) {
        setError(errMsg(e));
      } finally {
        setBusyId(null);
      }
    },
    [load],
  );

  const openMerge = (ev: QuarantinedEvent) => {
    setMergeId(ev.id);
    setMergeTarget(ev.duplicate_of ?? "");
  };

  return (
    <div>
      <h1 className="text-xl font-semibold">Review</h1>
      <p className="mt-1 text-sm text-zinc-500">
        Quarantined events the pipeline held back — publish, reject, or merge each
        into its probable original.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-zinc-500"
        >
          <option value="">All reasons</option>
          {REASONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>

        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search quarantined events…"
          className="flex-1 min-w-48 rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-500"
          onKeyDown={(e) => e.key === "Enter" && load(0, false)}
        />
        <button
          onClick={() => load(0, false)}
          disabled={loading}
          className="rounded bg-zinc-700 px-3 py-1.5 text-sm text-white hover:bg-zinc-600 disabled:opacity-50"
        >
          {loading ? "Loading…" : "Search"}
        </button>

        {total !== null && (
          <span className="ml-auto text-xs text-zinc-500">{total} in quarantine</span>
        )}
      </div>

      {error && (
        <div className="mt-3 rounded border border-red-900 bg-red-950 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="mt-4 overflow-x-auto rounded-lg border border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 text-left text-xs uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="px-3 py-2 font-medium">Name</th>
              <th className="px-3 py-2 font-medium">Place</th>
              <th className="px-3 py-2 font-medium">Start</th>
              <th className="px-3 py-2 font-medium">Type</th>
              <th className="px-3 py-2 font-medium">Reason</th>
              <th className="px-3 py-2 font-medium">Confidence</th>
              <th className="px-3 py-2 font-medium">Created</th>
              <th className="px-3 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev) => (
              <Fragment key={ev.id}>
                <tr className="border-t border-zinc-800 align-top">
                  <td className="px-3 py-2 text-zinc-300 max-w-xs truncate">
                    {String(ev.props.name ?? "—")}
                  </td>
                  <td className="px-3 py-2 text-zinc-400 text-xs max-w-40 truncate">
                    {ev.place_name ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-zinc-300 text-xs whitespace-nowrap">
                    {ev.props.start ? new Date(String(ev.props.start)).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-2 text-zinc-400 text-xs">
                    {String(ev.props.type ?? "—")}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      title={ev.quarantine_reason}
                      className={`inline-block rounded px-2 py-0.5 text-xs whitespace-nowrap ${reasonBadgeClass(
                        ev.quarantine_reason,
                      )}`}
                    >
                      {REASON_LABELS[ev.quarantine_reason] ?? ev.quarantine_reason}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <ConfidenceChips confidence={ev.confidence} />
                  </td>
                  <td className="px-3 py-2 text-zinc-400 text-xs whitespace-nowrap">
                    {new Date(ev.created).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex gap-1">
                      <button
                        onClick={() => resolve(ev.id, "publish")}
                        disabled={busyId === ev.id}
                        className="rounded bg-emerald-800 px-2 py-0.5 text-xs text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        Publish
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm("Reject (delete) this quarantined event?")) {
                            resolve(ev.id, "reject");
                          }
                        }}
                        disabled={busyId === ev.id}
                        className="rounded bg-red-900 px-2 py-0.5 text-xs text-white hover:bg-red-800 disabled:opacity-50"
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => (mergeId === ev.id ? setMergeId(null) : openMerge(ev))}
                        disabled={busyId === ev.id}
                        className="rounded bg-zinc-700 px-2 py-0.5 text-xs text-white hover:bg-zinc-600 disabled:opacity-50"
                      >
                        Merge…
                      </button>
                    </div>
                  </td>
                </tr>
                {mergeId === ev.id && (
                  <tr className="border-t border-zinc-800/50 bg-zinc-900/40">
                    <td colSpan={8} className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs text-zinc-400">
                          Merge into event id
                          {ev.duplicate_of && (
                            <span className="ml-1 text-zinc-500">
                              (pre-filled from probable original)
                            </span>
                          )}
                          :
                        </span>
                        <input
                          type="text"
                          value={mergeTarget}
                          onChange={(e) => setMergeTarget(e.target.value)}
                          placeholder="target event uuid"
                          className="w-80 rounded border border-zinc-700 bg-zinc-800 px-2 py-1 font-mono text-xs text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-500"
                        />
                        <button
                          onClick={() => resolve(ev.id, "merge_into", mergeTarget.trim())}
                          disabled={busyId === ev.id || !mergeTarget.trim() || mergeTarget.trim() === ev.id}
                          className="rounded bg-zinc-700 px-2 py-1 text-xs text-white hover:bg-zinc-600 disabled:opacity-50"
                        >
                          Merge
                        </button>
                        <button
                          onClick={() => setMergeId(null)}
                          className="rounded px-2 py-1 text-xs text-zinc-400 hover:text-zinc-200"
                        >
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {events.length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="px-3 py-4 text-center text-sm text-zinc-500">
                  Quarantine is empty. 🎉
                </td>
              </tr>
            )}
            {loading && events.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-4 text-center text-sm text-zinc-500">
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <button
          onClick={() => load(offset, true)}
          disabled={loading}
          className="mt-3 rounded bg-zinc-700 px-3 py-1.5 text-sm text-white hover:bg-zinc-600 disabled:opacity-50"
        >
          {loading ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
  );
}
